'use client';

import { useState } from 'react';
import { errorLogger } from '@/lib/errorLogger';
import { MessageCircleWarning, Send, X, CheckCircle, AlertTriangle } from 'lucide-react';

interface BugReportButtonProps {
  /** Position of the floating button */
  position?: 'bottom-right' | 'bottom-left';
  /** Show as floating button or inline */
  variant?: 'floating' | 'inline';
}

export default function BugReportButton({ 
  position = 'bottom-right',
  variant = 'floating'
}: BugReportButtonProps) {
  const [showModal, setShowModal] = useState(false);
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');

  const handleSendReport = async () => {
    if (!description.trim()) {
      return; // Require description
    }

    setStatus('sending');
    try {
      console.log('BugReportButton: Starting bug report submission...');
      const reportId = await errorLogger.saveBugReport(description);
      console.log('BugReportButton: Bug report submitted successfully, ID:', reportId);
      setStatus('success');
      // Clear logs after successful submit to start fresh
      errorLogger.clearLogs();
    } catch (e) {
      console.error('BugReportButton: Failed to send bug report:', e);
      setStatus('error');
    }
  };

  const handleCopyToClipboard = async () => {
    try {
      const reportText = errorLogger.getBugReportString(description);
      await navigator.clipboard.writeText(reportText);
      alert('Bug report copied to clipboard! You can paste it in an email or message.');
    } catch (e) {
      console.error('Failed to copy to clipboard:', e);
      alert('Failed to copy to clipboard. Please try again.');
    }
  };

  const handleClose = () => {
    setShowModal(false);
    setDescription('');
    setStatus('idle');
  };

  // Position above the bottom navigation bar (which is ~70px tall)
  const positionClasses = position === 'bottom-right' 
    ? 'bottom-20 right-4' 
    : 'bottom-20 left-4';

  // Check if there are recent errors to show a dot indicator
  const logs = errorLogger.getLogs();
  const hasRecentErrors = logs.some(l => l.level === 'error');

  const button = (
    <button
      onClick={() => setShowModal(true)}
      className={variant === 'floating' 
        ? `fixed ${positionClasses} z-40 p-3 bg-gray-700/90 hover:bg-gray-600 text-white rounded-full shadow-lg transition-all hover:scale-105 group`
        : 'px-4 py-2 bg-gray-700/50 border border-gray-600 text-gray-300 rounded font-semibold text-sm hover:bg-gray-700 transition flex items-center gap-2'
      }
      title="Report a Problem"
    >
      <MessageCircleWarning size={variant === 'floating' ? 22 : 16} className="group-hover:text-operator-gold transition" />
      {variant === 'inline' && <span>Report Problem</span>}
      {/* Error indicator dot */}
      {hasRecentErrors && variant === 'floating' && (
        <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-pulse" />
      )}
    </button>
  );

  if (!showModal) return button;

  return (
    <>
      {button}
      
      {/* Modal - Clean, user-friendly design */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
        <div className="w-full max-w-md bg-operator-gunmetal rounded-xl border border-operator-steel shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="p-4 border-b border-operator-steel flex items-center justify-between bg-operator-void">
            <div className="flex items-center gap-3">
              <MessageCircleWarning className="text-operator-gold" size={24} />
              <h2 className="text-lg font-bold text-white">Report a Problem</h2>
            </div>
            <button
              onClick={handleClose}
              className="p-2 text-gray-400 hover:text-white transition rounded-lg hover:bg-white/10"
            >
              <X size={20} />
            </button>
          </div>

          {/* Content */}
          <div className="p-5">
            {status === 'success' ? (
              <div className="text-center py-6">
                <CheckCircle className="mx-auto text-green-400 mb-4" size={56} />
                <h3 className="text-xl font-bold text-white mb-2">Thanks for the feedback!</h3>
                <p className="text-gray-400">
                  We've received your report and will look into it.
                </p>
              </div>
            ) : status === 'error' ? (
              <div className="text-center py-6">
                <AlertTriangle className="mx-auto text-red-400 mb-4" size={56} />
                <h3 className="text-xl font-bold text-white mb-2">Couldn't send report</h3>
                <p className="text-gray-400 mb-4">
                  No worries! You can copy the report and send it to us directly.
                </p>
                <div className="flex flex-col gap-2">
                  <button
                    onClick={handleCopyToClipboard}
                    className="w-full py-3 px-4 bg-operator-gold text-operator-gunmetal rounded-lg font-bold hover:bg-operator-gold/90 transition"
                  >
                     Copy Report
                  </button>
                  <button
                    onClick={() => setStatus('idle')}
                    className="text-operator-gold hover:underline text-sm"
                  >
                    Try sending again
                  </button>
                </div>
              </div>
            ) : (
              <>
                {/* Friendly intro */}
                <p className="text-gray-300 text-sm mb-4">
                  Something not working right? Let us know and we'll fix it ASAP.
                </p>

                {/* Description */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    What went wrong?
                  </label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Tell us what happened... e.g. 'I tried to log my workout but the save button didn't work'"
                    className="w-full h-32 px-4 py-3 bg-operator-void border border-operator-steel text-white rounded-lg focus:outline-none focus:border-operator-gold transition resize-none placeholder:text-gray-500"
                    autoFocus
                  />
                </div>

                {/* Subtle note */}
                <p className="text-xs text-gray-500 mb-4">
                   We'll include some technical info to help us debug the issue.
                </p>

                {/* Send button */}
                <button
                  onClick={handleSendReport}
                  disabled={status === 'sending' || !description.trim()}
                  className="w-full py-3.5 px-4 bg-operator-gold text-operator-gunmetal rounded-lg font-bold flex items-center justify-center gap-2 hover:bg-operator-gold/90 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Send size={18} />
                  {status === 'sending' ? 'Sending...' : 'Send Report'}
                </button>
              </>
            )}
          </div>

          {/* Footer for success/error states */}
          {(status === 'success' || status === 'error') && (
            <div className="p-4 border-t border-operator-steel">
              <button
                onClick={handleClose}
                className="w-full py-3 px-4 bg-operator-steel/50 text-white rounded-lg font-semibold hover:bg-operator-steel transition"
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
